/**
 * PlayerActionPopover — advanced-scoring actions anchored to the tapped player icon.
 *
 *   [ errors (Opp+1, red) ]  ( player )  [ point-winning / stats (+1, green) ]
 *
 * - Opens beside the icon (portal + position: fixed), never off-screen.
 * - Narrow screens (< 600px): a compact sheet directly under the player row.
 * - Tap outside or Escape closes it without recording anything.
 * - Tapping an action records it immediately (onConfirm) and closes.
 * - Block (Assist): pick the main blocker from the on-court teammates.
 *
 * Props:
 *   anchorKey    string  — value of the icon's data-player-anchor attribute
 *   player       PlayerObject
 *   participant  'participant1' | 'participant2'
 *   scorer       BaseSportScoring | null
 *   teammates    PlayerObject[] — on-court players of the same side
 *   onConfirm    (player, metricKey, metric, extraPlayers?) => void
 *   onClose      () => void
 *   onSubOut     (participant, player) => void   — optional, shows "⇄ Sub out"
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ActionChipGroup } from './AdvancedScoringModal';
import PlayerAvatar, { playerDisplayName } from '../../scoreboards/common/PlayerAvatar';
import { SHARED_BLOCK_METRIC } from '../../../../config/sportsMetrics';

const MARGIN = 8;
const GAP = 6;
// Matches .adv-popover__center width in matches-live.css
export const CENTER_WIDTH = 72;
export const MOBILE_BREAKPOINT = 600;

const idOf = (p) => p?.id ?? p?.userId;
const escapeAttr = (v) => String(v).replace(/["\\]/g, '\\$&');
export const findAnchor = (anchorKey) =>
  document.querySelector(`[data-player-anchor="${escapeAttr(anchorKey)}"]`);

/** Errors / fouls / anything that gives the opponent a point go on the red side. */
export const splitMetrics = (metrics) => {
  const positive = [];
  const errors = [];
  metrics.forEach((m) => {
    (m.metric.awardsOpponentPoint || m.metric.isPositive === false ? errors : positive).push(m);
  });
  return { positive, errors };
};

/**
 * Pure placement maths.
 * @param {Object} anchor    DOMRect of the tapped icon
 * @param {Object} size      { leftWidth, rightWidth, height } of the two action columns
 * @param {Object} viewport  { width, height }
 * @returns {{ mode: 'split'|'side-right'|'side-left'|'sheet', left: number, top: number }}
 */
export const computePopoverPosition = ({ anchor, size, viewport, margin = MARGIN }) => {
  const clamp = (v, min, max) => Math.max(min, Math.min(v, Math.max(min, max)));

  if (viewport.width < MOBILE_BREAKPOINT) {
    const below = anchor.bottom + GAP;
    const top = below + size.height <= viewport.height - margin ? below : anchor.top - GAP - size.height;
    return { mode: 'sheet', left: margin, top: clamp(top, margin, viewport.height - margin - size.height) };
  }

  const top = clamp(anchor.top + anchor.height / 2 - size.height / 2, margin, viewport.height - margin - size.height);
  const splitWidth = size.leftWidth + CENTER_WIDTH + size.rightWidth;
  const splitLeft = anchor.left + anchor.width / 2 - CENTER_WIDTH / 2 - size.leftWidth;
  if (splitLeft >= margin && splitLeft + splitWidth <= viewport.width - margin) {
    return { mode: 'split', left: splitLeft, top };
  }

  // No room for one of the groups: put both on the side of the icon that has more space
  const sideWidth = size.leftWidth + size.rightWidth;
  const maxLeft = viewport.width - margin - sideWidth;
  if (viewport.width - anchor.right >= anchor.left) {
    return { mode: 'side-right', left: clamp(anchor.right + GAP, margin, maxLeft), top };
  }
  return { mode: 'side-left', left: clamp(anchor.left - GAP - sideWidth, margin, maxLeft), top };
};

const PlayerActionPopover = ({
  anchorKey,
  player,
  participant,
  scorer = null,
  teammates = [],
  onConfirm,
  onClose,
  onSubOut,
}) => {
  const rootRef = useRef(null);
  const errorsRef = useRef(null);
  const positiveRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [step, setStep] = useState('actions');
  const [pos, setPos] = useState(null);
  const stepRef = useRef(step);
  stepRef.current = step;

  const name = playerDisplayName(player);
  const quickMetrics = scorer?.getQuickMetrics ? scorer.getQuickMetrics(null) : [];
  const allowed = (key, p) => (scorer?.canRecordMetric?.(key, p) ?? { allowed: true }).allowed !== false;
  const metrics = quickMetrics.length > 0
    ? quickMetrics.filter(({ key }) => allowed(key, player))
    : [{ key: 'score', metric: { label: 'Score', affectsScore: true, isPositive: true } }];
  const { positive, errors } = splitMetrics(metrics);

  const focusAnchor = useCallback(() => {
    const el = findAnchor(anchorKey);
    if (el && typeof el.focus === 'function') el.focus();
  }, [anchorKey]);

  const reposition = useCallback(() => {
    const anchorEl = findAnchor(anchorKey);
    if (!anchorEl || anchorEl.disabled) {
      onCloseRef.current();
      return;
    }
    // The partner picker has no action columns to measure: keep the current spot
    if (stepRef.current === 'partner') return;
    const rootRect = rootRef.current?.getBoundingClientRect();
    const next = computePopoverPosition({
      anchor: anchorEl.getBoundingClientRect(),
      size: {
        leftWidth: errorsRef.current?.getBoundingClientRect().width || 0,
        rightWidth: positiveRef.current?.getBoundingClientRect().width || 0,
        height: rootRect?.height || 0,
      },
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
    setPos((prev) => (
      prev && prev.mode === next.mode && prev.left === next.left && prev.top === next.top ? prev : next
    ));
  }, [anchorKey]);

  // Re-measure after every render: the scoreboard may have moved or ended the set
  useLayoutEffect(() => { reposition(); });

  useEffect(() => {
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [reposition]);

  useEffect(() => {
    const onPointerDown = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target)) return;
      // Another player icon: its click switches (or toggles) the popover in one tap
      if (target?.closest?.('[data-player-anchor]')) return;
      onCloseRef.current();
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCloseRef.current();
      focusAnchor();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [focusAnchor]);

  useEffect(() => {
    rootRef.current?.querySelector('button:not(:disabled)')?.focus();
  }, [step]);

  const handleAction = (metricKey, metric) => {
    if (metricKey === 'blockAssist') {
      setStep('partner');
      return;
    }
    onConfirm(player, metricKey, metric);
    focusAnchor();
  };

  // Shared block: both blockers are credited a Block Assist and the team wins
  // one point (AS-B08). The partner's write carries the point.
  const handlePartner = (partner) => {
    onConfirm(partner, 'blockAssist', SHARED_BLOCK_METRIC, [player]);
    focusAnchor();
  };

  const mode = pos?.mode || 'split';
  const partners = teammates.filter((p) => String(idOf(p)) !== String(idOf(player)) && allowed('blockSolo', p));

  const renderChips = (list) => list.map(({ key, metric }) => (
    <ActionChipGroup key={key} metricKey={key} metric={metric} player={player} onSelect={handleAction} />
  ));

  const renderActions = () => (
    <>
      {mode !== 'split' && <div className="adv-popover__name">{name}</div>}
      <div className="adv-popover__body">
        {errors.length > 0 && (
          <div ref={errorsRef} className="adv-popover__col adv-popover__col--errors" aria-label="Errors (opponent point)">
            {renderChips(errors)}
          </div>
        )}
        {mode === 'split' && (
          <div className="adv-popover__center" aria-hidden="true">
            <PlayerAvatar player={player} />
            <span className="adv-popover__center-name">{name}</span>
          </div>
        )}
        <div ref={positiveRef} className="adv-popover__col adv-popover__col--positive" aria-label="Points and stats">
          {renderChips(positive)}
        </div>
      </div>
      {onSubOut && (
        <div className="adv-popover__footer">
          <button type="button" className="adv-metric-chip adv-popover__sub-chip" onClick={() => onSubOut(participant, player)}>
            ⇄ Sub out
          </button>
        </div>
      )}
    </>
  );

  const renderPartner = () => (
    <div className="adv-popover__partner">
      <div className="adv-popover__name">Block (Assist) — who blocked with {name.split(' ')[0]}?</div>
      {partners.length === 0 ? (
        <div className="adv-popover__empty">No eligible teammates on court.</div>
      ) : (
        <div className="adv-popover__partner-grid">
          {partners.map((p) => (
            <button
              key={idOf(p) ?? playerDisplayName(p)}
              type="button"
              className="vb-adv-player-card-mini"
              onClick={() => handlePartner(p)}
            >
              <PlayerAvatar player={p} />
              <span className="vb-adv-player-mini-name">{playerDisplayName(p)}</span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="adv-popover__back" onClick={() => setStep('actions')}>← Back</button>
    </div>
  );

  const content = (
    <div
      ref={rootRef}
      className={`adv-popover adv-popover--${mode} adv-popover--${participant}`}
      role="dialog"
      aria-label={`Actions for ${name}`}
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {step === 'partner' ? renderPartner() : renderActions()}
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default PlayerActionPopover;
